import React, { useState } from 'react';
import { useAsyncDebounce } from 'react-table';

import { locale } from '@trqk/utils';

import { Input } from '..';

interface GlobalFilterProps {
    /**
     * Rate at which the filter value will be debounced; i.e. it will not change more often than
     * this interval.
     */
    debounceInterval?: number;
    /**
     * Method for setting the state of the global filter; this is provided on the react-table table
     * instance when you pass in the useGlobalFilter hook to useTable.
     */
    setGlobalFilter(value: string | undefined): void;
    /**
     * State of the global filter; this is provided as state.globalFilter returned by the
     * react-table table instance when you pass in the useGlobalFilter hook to useTable.
     */
    globalFilter: string;
    /**
     * Placeholder for the <Input>
     * @defaultValue 'Search . . .'
     */
    placeholder?: string;
}

const defaultPlaceholder = locale.enUS.COMPONENTS.GLOBAL_FILTER.PLACEHOLDER;

/**
 * An input suitable for use in `react-table` as a global filter input.
 * @param props - {@link GlobalFilterProps}
 * @returns JSX
 */
export const GlobalFilter = ({
    debounceInterval = 300,
    globalFilter,
    setGlobalFilter,
    placeholder = defaultPlaceholder,
}: GlobalFilterProps): JSX.Element => {
    const [inputValue, setInputValue] = useState(globalFilter || '');

    const setGlobalFilterDebounced = useAsyncDebounce((value: string): void => {
        const finalValue = value || undefined; // Set undefined to remove the filter entirely

        setGlobalFilter(finalValue);
    }, debounceInterval);

    return (
        <span
            style={{
                alignSelf: 'end',
            }}
        >
            <Input
                addClasses="border border-tertiary-300 text-sm respond-to-mouse"
                icon="search"
                inputId="global-search-input"
                padding="small"
                value={inputValue}
                handleChange={(nextValue: string) => {
                    setGlobalFilterDebounced(nextValue);
                    setInputValue(nextValue);
                }}
                placeholder={placeholder}
            />
        </span>
    );
};
